<template>
  <div id="yearly-budget">
    <div class="panel">
      <div class="panel-head">
        <h2>{{client.name}} - Yearly Budget</h2>
      </div>

      <div class="panel-content">
        <div id="year-selector">
          <a @click.prevent="prevYear" href="#">
            <i class="fa fa-arrow-left"></i> prev year
          </a>
          <a @click.prevent="nextYear" href="#">
            next year
            <i class="fa fa-arrow-right"></i>
          </a>
        </div>
        <table class="spreadsheet">
          <thead>
            <tr>
              <th>{{ getYear }}</th>
              <th v-for="(budget, i) in budgets" :key="i" :class="{ current: i + 1 == month }">
                <a
                  class="month-select"
                  :class="{'disabled': !budget.investment && budget.investment !== 0}"
                  href="#change-month"
                  @click.prevent="budget.investment || budget.investment == 0 ? changeMonth(i+1) : ''"
                >{{ shortMonth(budget.date_time) }}</a>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Investment</td>
              <td v-for="(month, i) in budgets" :key="i">
                <template v-if="month.investment || month.investment == 0">
                  <currency-input
                    :value="month.investment ? month.investment : 0"
                    :class="{ active: month.investment_override <= 0 }"
                    disabled
                  ></currency-input>
                </template>
                <template v-else>
                  <input disabled value="--" />
                </template>
              </td>
            </tr>
            <tr>
              <td>Override</td>
              <td
                v-for="(month, i) in budgets"
                :key="i"
                @mouseenter="showTooltip(i)"
                @mouseleave="hideTooltip(i)"
                class="override"
              >
                <template v-if="month.investment_override || month.investment_override == 0">
                  <div
                    v-if="month.investment_override > 0 && month.override_reason !== null"
                    v-show="month.show_tooltip"
                    class="tooltip"
                  >
                    <i class="fa fa-info-circle"></i>
                    {{ month.override_reason }}
                  </div>
                  <currency-input
                    @input="unsaved = true; reasonRequired = month.investment_override > 0"
                    v-model="month.investment_override"
                    :class="{ active: month.investment_override > 0 }"
                  ></currency-input>
                </template>
                <template v-else>
                  <input disabled value="--" />
                </template>
              </td>
            </tr>
          </tbody>
        </table>

        <div class="group">
          <div class="form-control flags">
            <fieldset>
              <legend>Flags</legend>
              <label>
                <input
                  v-model="client.zero_balance"
                  @change="unsaved = true"
                  type="checkbox"
                  value="1"
                /> Go to zero balance
              </label>
              <label>
                <input
                  v-model="client.client_card"
                  @change="unsaved = true"
                  type="checkbox"
                  value="1"
                /> Use client credit card
              </label>
              <label>
                <input
                  v-model="client.is_using_google_post_pay"
                  @change="unsaved = true"
                  type="checkbox"
                  value="1"
                /> Using Google Post Pay
              </label>
            </fieldset>
          </div>

          <div class="totals">
            <table class="spreadsheet">
              <thead>
                <tr>
                  <th>{{ year }} Total</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <currency-input :value="yearTotal" disabled></currency-input>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div v-show="unsaved && reasonRequired " class="form-control">
          <label for="reason">Override Reason</label>
          <input v-model="reason" id="reason" v-validate="'required'" name="reason" type="text" />
          <div v-show="errors.has('reason')" class="validation-error">{{ errors.first('reason') }}</div>
        </div>

        <button
          @click="save()"
          class="button"
          :class="{ 'button-green': !unsaved, 'button-red': unsaved }"
        >Save Changes</button>
      </div>
    </div>
  </div>
</template>

<script>
import CurrencyInput from "@/components/ui/CurrencyInput";
import { mapActions, mapGetters } from "vuex";
import moment from "moment";

export default {
  name: "YearlyBudget",
  props: ["budgets", "client"],
  components: {
    CurrencyInput
  },
  data() {
    return {
      unsaved: false,
      reason: null,
      reasonRequired: false,
      months: moment.months()
    };
  },
  computed: {
    ...mapGetters(["getYear", "getMonth"]),
    year: {
      get() {
        return this.getYear;
      },
      set(value) {
        this.setYear({ year: value });
        this.$emit("update");
      }
    },
    month: {
      get() {
        return this.getMonth;
      },
      set(value) {
        this.setMonth({ month: value });
        this.$emit("update");
      }
    },
    previousYear() {
      return +this.month == 1 ? +this.year - 1 : +this.year;
    },
    previousMonth() {
      return +this.month == 1 ? 12 : +this.month - 1;
    },
    yearTotal() {
      return this.budgets.reduce((carry, item) => {
        let add = item.investment_override > 0
          ? item.investment_override
          : item.investment ? item.investment : 0;
        return carry + add;
      }, 0);
    }
  },
  methods: {
    ...mapActions(["setYear", "setMonth"]),
    prevYear() {
      this.year = parseInt(this.year) - 1;
    },
    nextYear() {
      this.year = parseInt(this.year) + 1;
    },
    changeMonth(monthNum) {
      this.month = monthNum;
    },
    showTooltip(i) {
      let month = this.budgets[i];
      month.show_tooltip = true;
      this.$set(this.budgets, i, month);
    },
    hideTooltip(i) {
      let month = this.budgets[i];
      month.show_tooltip = false;
      this.$set(this.budgets, i, month);
    },
    shortMonth(date) {
      return moment.utc(date, "YYYY-MM-DD").format("MMM");
    },
    async save() {
      let valid = await this.$validator.validateAll();
      if (!valid && this.reasonRequired) {
        return false;
      } else {
        let validBudgets = [];
        this.budgets.forEach(budget => {
          return budget.investment || budget.investment == 0 ? validBudgets.push(budget) : '';
        });
        let response = await this.$http.put(
          "/paid-media-budgets/" + this.client.id,
          {
            budgets: validBudgets,
            client: this.client,
            reason: this.reason
          }
        );
      }

      this.reason = null;
      this.$emit("update");
      this.unsaved = false;
    }
  }
};
</script>

<style lang="scss">
#yearly-budget {
  #year-selector {
    display: flex;
    justify-content: space-between;
    margin: 0 10px 15px;

    a {
      font-weight: 700;
      font-size: 12px;
      text-transform: uppercase;
    }
  }
  .month {
    width: 25%;
    margin-bottom: 20px;
  }
  .month-select {
    color: $white;
    &:not(.disabled):hover {
      color: $orange;
    }
    &.disabled:hover {
      cursor: not-allowed;
    }
  }

  .spreadsheet {
    @keyframes pulse {
      0% {
        background-color: $medium-blue;
        border-color: $medium-blue;
      }
      50% {
        background-color: $orange;
        border-color: $orange;
      }
      100% {
        background-color: $medium-blue;
        border-color: $medium-blue;
      }
    }
    th {
      padding: 2px;
      font-size: 12px;
      text-align: center;
      text-transform: uppercase;
      background: $medium-blue;
      border: 1px solid $medium-blue;
      color: $white;

      &.current {
        background: $orange;
        border-color: $orange;
        // animation-name: pulse;
        // animation-duration: 1s;
        // animation-iteration-count: infinite;
      }
    }

    td {
      border: 1px solid $medium-gray;
      padding: 0;

      &:first-of-type {
        padding: 4px 12px 4px 4px;
        font-size: 12px;
        font-weight: 700;
        text-transform: uppercase;
        background: $light-gray;
      }
    }

    input {
      appearance: none;
      display: block;
      margin: 0;
      width: 100%;
      height: auto;
      padding: 4px 2px;
      font-size: 12px;
      line-height: 1.5;
      color: darken($black, 30%);
      background-color: $white;
      background-image: none;
      border: 2px solid transparent;
      border-radius: 0;
      transition: none;
      text-align: right;
      color: $black;

      &:disabled {
        background: $light-gray;
      }

      &.active {
        background: #d4eaf6;
      }
    }

    .override {
      position: relative;
    }

    .tooltip {
      position: absolute;
      top: 36px;
      left: 50%;
      transform: translateX(-50%);
      padding: 5px 10px;
      background: $dark-blue;
      border-radius: 3px;
      color: $white;
      box-shadow: 0 2px 4px $black;
      z-index: 10;
      width: 320px;
      i {
        margin-right: 5px;
      }
    }
  }

  .group {
    display: flex;
    justify-content: space-between;
  }

  .totals {
    margin-top: 15px;
    width: 25%;

    input {
      font-size: 18px;
      font-weight: 700;
      text-align: center;
    }
  }

  .flags {
    width: 25%;
  }
}
</style>
